import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import CommunityCard, { CommunityCardData } from "@/components/community/CommunityCard";

interface CommunityWithOwnership extends CommunityCardData {
  is_owner: boolean;
}

const ComunidadesQueParticipo = () => {
  const [communities, setCommunities] = useState<CommunityWithOwnership[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchMentorCommunities();
    }
  }, [user]);

  const fetchMentorCommunities = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      console.log("🔍 [ComunidadesQueParticipo] Buscando comunidades do mentor:", user?.id);
      setLoading(true);
      
      // Buscar community IDs primeiro com tratamento de erro mais robusto
      const { data: membershipData, error: membershipError } = await supabase
        .from("community_memberships")
        .select("community_id")
        .eq("user_id", user.id)
        .eq("status", "active");

      if (membershipError) {
        console.error("❌ [ComunidadesQueParticipo] Erro ao buscar memberships:", membershipError);
        
        // Se a tabela não existir, retornamos vazio ao invés de erro
        if (membershipError.code === '42P01') {
          console.warn("⚠️ [ComunidadesQueParticipo] Tabela community_memberships não existe, retornando vazio");
          setCommunities([]);
          return;
        }
        
        throw membershipError;
      }

      console.log("✅ [ComunidadesQueParticipo] Memberships encontrados:", membershipData?.length || 0);

      if (!membershipData || membershipData.length === 0) {
        console.log("📋 [ComunidadesQueParticipo] Nenhum membership encontrado");
        setCommunities([]);
        return;
      }

      // Extrair IDs das comunidades
      const communityIds = membershipData.map(m => m.community_id).filter(Boolean);

      if (communityIds.length === 0) {
        console.log("📋 [ComunidadesQueParticipo] Nenhum ID de comunidade válido");
        setCommunities([]);
        return;
      }

      // Buscar comunidades com os IDs encontrados
      const { data: communitiesData, error: communitiesError } = await supabase
        .from("communities")
        .select(`
          id,
          name,
          description,
          cover_image_url,
          cover_image,
          member_count,
          is_featured,
          is_active,
          created_at,
          owner_id,
          mentor_id,
          slug,
          game_id
        `)
        .in("id", communityIds)
        .eq("is_active", true);

      if (communitiesError) {
        console.error("❌ [ComunidadesQueParticipo] Erro ao buscar comunidades:", communitiesError);
        throw communitiesError;
      }

      console.log("✅ [ComunidadesQueParticipo] Dados das comunidades:", communitiesData?.length || 0);

      if (!communitiesData || communitiesData.length === 0) {
        console.log("📋 [ComunidadesQueParticipo] Nenhuma comunidade encontrada");
        setCommunities([]);
        return;
      }

      // Buscar dados dos jogos
      const gameIds = communitiesData
        .map(c => c.game_id)
        .filter(Boolean)
        .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicatas

      let gamesData = [];
      if (gameIds.length > 0) {
        const { data: games, error: gamesError } = await supabase
          .from("games")
          .select("id, name")
          .in("id", gameIds);

        if (gamesError) {
          console.warn("⚠️ [ComunidadesQueParticipo] Erro ao buscar games:", gamesError);
          // Continua sem dados de games ao invés de falhar
        } else {
          gamesData = games || [];
        }
      }

      // Buscar dados dos mentores/owners com tratamento melhorado
      const ownerIds = communitiesData
        .map(c => c.owner_id || c.mentor_id)
        .filter(Boolean)
        .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicatas

      let profilesData = [];
      if (ownerIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, nickname")
          .in("id", ownerIds);

        if (profilesError) {
          console.warn("⚠️ [ComunidadesQueParticipo] Erro ao buscar profiles:", profilesError);
          // Continua sem dados de profile ao invés de falhar
        } else {
          // Buscar imagens dos mentores
          const profilesWithImages = [];
          for (const profile of profiles || []) {
            let mentorImageUrl = null;
            
            // Tentar buscar imagem do profiles primeiro
            try {
              const { data: profileData, error: profileError } = await supabase
                .from("profiles")
                .select("image_url")
                .eq("id", profile.id)
                .single();

              if (!profileError && profileData?.image_url) {
                mentorImageUrl = profileData.image_url;
                console.log(`📸 [ComunidadesQueParticipo] Imagem encontrada em profiles para ${profile.nickname}:`, mentorImageUrl);
              }
            } catch (error) {
              console.log(`⚠️ [ComunidadesQueParticipo] Erro ao buscar imagem em profiles para ${profile.nickname}`);
            }

            // Se não encontrou, tentar gerar URL do storage baseado no padrão conhecido
            if (!mentorImageUrl) {
              // URLs conhecidas para mentores específicos
              const knownMentorImages = {
                'b0e6f620-12b2-487c-a55c-60754ae4d9b9': 'https://vcjgzqjktnpdjeunnhme.supabase.co/storage/v1/object/public/profile-images/b0e6f620-12b2-487c-a55c-60754ae4d9b9/profile-6e789854-22ce-4ab8-91f4-c40fe1eacc96.png'
              };
              
              if (knownMentorImages[profile.id]) {
                mentorImageUrl = knownMentorImages[profile.id];
                console.log(`📸 [ComunidadesQueParticipo] Usando URL conhecida para ${profile.nickname}:`, mentorImageUrl);
              }
            }

            profilesWithImages.push({
              ...profile,
              image_url: mentorImageUrl
            });
          }
          profilesData = profilesWithImages;
        }
      }

      console.log("✅ [ComunidadesQueParticipo] Dados dos games:", gamesData.length);
      console.log("✅ [ComunidadesQueParticipo] Dados dos profiles:", profilesData.length);

      // Transformar os dados para o formato esperado
      const formattedCommunities: CommunityWithOwnership[] = communitiesData.map(community => {
        const ownerId = community.owner_id || community.mentor_id;
        const profile = profilesData?.find(p => p.id === ownerId);
        const game = gamesData?.find(g => g.id === community.game_id);
        const isOwner = ownerId === user?.id;
        
        // Usar cover_image se cover_image_url não existir (para compatibilidade)
        const coverImageUrl = community.cover_image_url || community.cover_image;
        
        return {
          id: community.id,
          name: community.name || "Comunidade sem nome",
          slug: community.slug || community.id,
          description: community.description || "",
          cover_url: coverImageUrl,
          cover_image_url: coverImageUrl,
          member_count: community.member_count || 0,
          is_featured: community.is_featured || false,
          created_at: community.created_at,
          game: {
            name: game?.name || "Jogo não especificado"
          },
          mentor: {
            profile: {
              nickname: profile?.nickname || "Mentor",
              image_url: profile?.image_url
            }
          },
          is_owner: isOwner
        };
      });

      setCommunities(formattedCommunities);
      console.log("📋 [ComunidadesQueParticipo] Comunidades formatadas:", formattedCommunities.length);
      
    } catch (error: any) {
      console.error("💥 [ComunidadesQueParticipo] Erro geral:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar comunidades",
        description: error?.message || "Não foi possível carregar suas comunidades. Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVisitCommunity = (communityId: string) => {
    const community = communities.find(c => c.id === communityId);
    if (community?.slug) {
      console.log("🔗 [ComunidadesQueParticipo] Navegando com slug:", `/comunidades/${community.slug}`);
      navigate(`/comunidades/${community.slug}`);
    } else {
      console.log("🔗 [ComunidadesQueParticipo] Navegando sem slug:", `/comunidade/${communityId}`);
      navigate(`/comunidade/${communityId}`);
    }
  };

  const handleCreateCommunity = () => {
    navigate("/painel-mentor/comunidades/criar");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Comunidades que Participo</h1>
          <p className="text-gray-400 mt-1 text-sm md:text-base">
            Comunidades onde você é membro (criadas por você ou outros mentores)
          </p>
        </div>
        <Button 
          onClick={handleCreateCommunity}
          className="bg-[#00B6F0] text-black hover:bg-[#00A3D9] font-medium self-start sm:self-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          Criar Comunidade
        </Button>
      </div>

      {communities.length === 0 ? (
        <Card className="bg-[#1A2233] border-[#222A35] text-white">
          <CardContent className="p-6 md:p-8 text-center">
            <Users className="h-12 md:h-16 w-12 md:w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg md:text-xl font-semibold mb-2">
              Nenhuma comunidade encontrada
            </h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto text-sm md:text-base">
              Você ainda não faz parte de nenhuma comunidade. Explore e junte-se a comunidades 
              ou crie sua própria comunidade para conectar-se com outros jogadores.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/comunidades")}
                variant="outline"
                className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
              >
                <Users className="mr-2 h-4 w-4" />
                Explorar Comunidades
              </Button>
              <Button 
                onClick={handleCreateCommunity}
                className="bg-[#00B6F0] text-black hover:bg-[#00A3D9] font-medium"
              >
                <Plus className="mr-2 h-4 w-4" />
                Criar Comunidade
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {communities.map((community) => (
            <CommunityCard
              key={community.id}
              community={community}
              showOwnerBadge={true}
              showMentorInfo={true}
              onVisit={handleVisitCommunity}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ComunidadesQueParticipo; 